/**
 * Silent one-shot OAuth restart when Supabase/Google report a consumed auth code.
 * Common on first login behind corporate proxies that prefetch callback URLs.
 */

import { supabase } from '../../lib/supabase'
import { logger } from '../../helpers/logger.helper'
import { authLiveDebug } from './authLiveDebug.helper'
import { getOAuthRedirectUrls } from './signInApi'
import { clearStaleLocalSessionBeforeOAuth } from './clearStaleOAuthSession.helper'
import { releaseOAuthPkceExchangeLock } from './oauthPkceExchange.helper'
import {
  markOAuthAutoRetryAttempted,
  readOAuthRestartConfig,
  shouldSilentlyAutoRetryOAuthCallback,
} from './oauthAutoRetryStorage.helper'

export type { OAuthRestartConfig, OAuthRestartProvider } from './oauthAutoRetryStorage.helper'
export {
  clearOAuthAutoRetryState,
  persistOAuthRestartConfig,
} from './oauthAutoRetryStorage.helper'

let silentOAuthAutoRetryStarted = false

/**
 * Restarts the stored OAuth flow once. Returns true when a browser redirect was started.
 */
export async function restartOAuthFromStoredConfig(): Promise<boolean> {
  if (typeof window === 'undefined') return false
  const config = readOAuthRestartConfig()
  if (!config) return false

  await clearStaleLocalSessionBeforeOAuth()
  releaseOAuthPkceExchangeLock()

  const redirectTo = getOAuthRedirectUrls()?.redirectUrl ?? undefined
  const oauthOpts = {
    provider: config.provider,
    options: {
      ...(redirectTo ? { redirectTo } : {}),
      ...(config.queryParams ? { queryParams: config.queryParams } : {}),
    },
  }

  authLiveDebug('restartOAuthFromStoredConfig', {
    provider: config.provider,
    useLinkIdentity: config.useLinkIdentity === true,
  })

  const { data, error } =
    config.useLinkIdentity === true
      ? await supabase.auth.linkIdentity(oauthOpts)
      : await supabase.auth.signInWithOAuth(oauthOpts)

  if (error || !data?.url) {
    logger.error('[OAuth] auto-retry could not restart provider flow', {
      provider: config.provider,
      message: error?.message,
    })
    return false
  }

  window.location.replace(data.url)
  return true
}

export async function attemptSilentOAuthAutoRetry(
  error: string,
  errorCode: string | null | undefined,
  errorDescription: string | null | undefined,
  isCodeReuseFailure: (params: {
    error: string
    errorCode?: string | null
    errorDescription?: string | null
  }) => boolean,
): Promise<boolean> {
  if (silentOAuthAutoRetryStarted) return false
  if (!shouldSilentlyAutoRetryOAuthCallback(error, errorCode, errorDescription, isCodeReuseFailure)) {
    return false
  }
  silentOAuthAutoRetryStarted = true
  markOAuthAutoRetryAttempted()
  authLiveDebug('OAuthCallback silent auto-retry START', { error, errorCode })
  logger.warn('[OAuth] silent auto-retry after consumed auth code', { error, errorCode })
  return restartOAuthFromStoredConfig()
}
